<template>
  <div class="page-outer">
    <div class="page-header page-header-member-detail">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleBack">返回</button>
        <button class="btn btn-primary" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadMember">重試</button>
    </div>

    <div v-else-if="member" class="detail-content">
      <!-- Tab Navigation -->
      <div class="tabs-nav">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          class="tab-btn"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon"></i>
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tabs-content">
        <!-- 基本資訊 Tab -->
        <div v-show="activeTab === 'basic'" class="tab-pane">
          <div class="info-section">
            <h3 class="section-title">基本資訊</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">會員編號：</span>
                <span class="info-value">{{ member.memberCode || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">姓名：</span>
                <span class="info-value">{{ member.name || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">手機號碼：</span>
                <span class="info-value">{{ member.phone || '-' }}</span>
                <span v-if="member.phoneVerified" class="badge badge-success" style="margin-left: 8px;">
                  已驗證
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">Email：</span>
                <span class="info-value">{{ member.email || '-' }}</span>
                <span v-if="member.emailVerified" class="badge badge-success" style="margin-left: 8px;">
                  已驗證
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">生日：</span>
                <span class="info-value">{{ formatDate(member.birthday) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">性別：</span>
                <span class="info-value">{{ getGenderText(member.gender) }}</span>
              </div>
              <div class="info-item full-width">
                <span class="info-label">地址：</span>
                <span class="info-value">
                  <span v-if="member.addressCity || member.addressDistrict || member.addressStreet">
                    {{ getFullAddress(member) }}
                  </span>
                  <span v-else>-</span>
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">LINE 綁定：</span>
                <span class="info-value">
                  <span v-if="member.lineBound" class="badge badge-success">已綁定</span>
                  <span v-else class="badge badge-secondary">未綁定</span>
                </span>
              </div>
              <div v-if="member.lineBound && member.lineDisplayName" class="info-item">
                <span class="info-label">LINE 名稱：</span>
                <span class="info-value">{{ member.lineDisplayName }}</span>
              </div>
              <div v-if="member.lineBound && member.lineUserId" class="info-item">
                <span class="info-label">LINE User ID：</span>
                <span class="info-value"><code>{{ member.lineUserId }}</code></span>
              </div>
            </div>
          </div>

          <div class="info-section">
            <h3 class="section-title">會員狀態</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">會員等級：</span>
                <span v-if="member.tierName" class="badge badge-info">
                  {{ member.tierName }}
                </span>
                <span v-else class="badge badge-secondary">-</span>
              </div>
              <div class="info-item">
                <span class="info-label" title="進入當前等級的時間">升等日期：</span>
                <span class="info-value">{{ formatDate(member.tierStartedAt) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">狀態：</span>
                <span :class="getStatusBadgeClass(member.status)">
                  {{ getStatusText(member.status) }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">註冊方式：</span>
                <span class="info-value">{{ getRegistrationMethodText(member.registrationMethod) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">註冊時間：</span>
                <span class="info-value">{{ formatDate(member.registeredAt || member.createdAt) }}</span>
              </div>
            </div>
          </div>

          <div class="info-section">
            <h3 class="section-title">消費統計</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">累積消費金額：</span>
                <span class="info-value highlight">{{ formatCurrency(member.totalSpent) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">消費次數：</span>
                <span class="info-value">{{ formatNumber(member.purchaseCount) }} 次</span>
              </div>
              <div class="info-item">
                <span class="info-label">最後消費時間：</span>
                <span class="info-value">{{ formatDate(member.lastPurchaseAt) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 點數管理 Tab -->
        <div v-show="activeTab === 'points'" class="tab-pane">
          <div class="info-section">
            <div class="section-header">
              <h3 class="section-title">點數統計</h3>
              <button class="btn btn-primary btn-sm" @click="showAdjustPointsModal = true">
                <i class="ri-add-line"></i>
                調整點數
              </button>
            </div>
            <div class="info-grid points-stats-grid">
              <div class="info-item">
                <span class="info-label">累積總點數：</span>
                <span class="info-value highlight">{{ formatNumber(member.totalPoints) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">可用點數：</span>
                <span class="info-value highlight">{{ formatNumber(pointsBalance?.availablePoints ?? member.availablePoints) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">待生效點數：</span>
                <span class="info-value" :class="{ 'text-warning-strong': (pointsBalance?.pendingPoints || 0) > 0 }">
                  {{ formatNumber(pointsBalance?.pendingPoints || 0) }}
                </span>
              </div>
            </div>
          </div>

          <div class="info-section">
            <div class="section-header">
              <h3 class="section-title">點數交易記錄</h3>
              <div class="filter-group">
                <select v-model="pointsFilter.type" class="form-control" @change="loadPointsTransactions">
                  <option value="">所有類型</option>
                  <option value="earn">獲得</option>
                  <option value="redeem">兌換</option>
                  <option value="expire">過期</option>
                  <option value="adjust">調整</option>
                </select>
              </div>
            </div>
            
            <div v-if="pointsLoading" class="loading-state">
              <div class="spinner-sm"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="pointsError" class="error-state">
              <p class="error-message">{{ pointsError }}</p>
            </div>
            <div v-else-if="pointsTransactions.length > 0" class="table-responsive">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>時間</th>
                    <th>類型</th>
                    <th>點數變化</th>
                    <th>可用餘額</th>
                    <th>生效日期</th>
                    <th>說明</th>
                    <th>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="transaction in pointsTransactions" :key="transaction.id">
                    <td>{{ formatDateTime(transaction.createdAt) }}</td>
                    <td>{{ getTransactionTypeText(transaction.transactionType) }}</td>
                    <td :class="transaction.points > 0 ? 'text-success' : 'text-danger'">
                      {{ transaction.points > 0 ? '+' : '' }}{{ formatNumber(transaction.points) }}
                    </td>
                    <td>{{ formatNumber(transaction.balance) }}</td>
                    <td>{{ formatEffectiveDate(transaction.effectiveDate) }}</td>
                    <td>{{ transaction.description || '-' }}</td>
                    <td>
                      <span :class="getTransactionStatusBadge(transaction.status)">
                        {{ getTransactionStatusText(transaction.status, transaction.effectiveDate) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <p>暫無點數交易記錄</p>
            </div>
          </div>
        </div>

        <!-- 優惠券 Tab -->
        <div v-show="activeTab === 'coupons'" class="tab-pane">
          <div class="info-section">
            <div class="section-header">
              <h3 class="section-title">優惠券列表</h3>
              <button class="btn btn-primary btn-sm" @click="showIssueCouponModal = true">
                <i class="ri-ticket-line"></i>
                發放優惠券
              </button>
            </div>

            <div class="filter-group" style="margin-bottom: 16px;">
              <select v-model="couponsFilter.status" class="form-control" @change="loadCoupons">
                <option value="">所有狀態</option>
                <option value="unused">未使用</option>
                <option value="used">已使用</option>
                <option value="expired">已過期</option>
                <option value="cancelled">已取消</option>
                <option value="deleted">已刪除</option>
              </select>
            </div>

            <div v-if="couponsLoading" class="loading-state">
              <div class="spinner-sm"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="couponsError" class="error-state">
              <p class="error-message">{{ couponsError }}</p>
            </div>
            <div v-else-if="coupons.length > 0" class="table-responsive">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>優惠券名稱</th>
                    <th>類型</th>
                    <th>發放時間</th>
                    <th>使用時間</th>
                    <th>發放方式</th>
                    <th>狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="coupon in coupons" :key="coupon.id">
                    <td>{{ coupon.templateName }}</td>
                    <td>{{ getCouponTypeText(coupon.couponType) }}</td>
                    <td>{{ formatDateTime(coupon.issuedAt) }}</td>
                    <td>
                      <span v-if="coupon.usedAt">{{ formatDateTime(coupon.usedAt) }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>{{ getCouponIssueMethodText(coupon.issueMethod) }}</td>
                    <td>
                      <span :class="getCouponStatusBadge(coupon.status)">
                        {{ getCouponStatusText(coupon.status) }}
                      </span>
                      <div v-if="coupon.cancelReason" class="cancel-reason-inline">
                        {{ coupon.cancelReason }}
                      </div>
                    </td>
                    <td>
                      <div class="action-buttons">
                        <button 
                          class="btn btn-sm btn-info" 
                          @click="viewCouponDetail(coupon)"
                          title="查看詳情"
                        >
                          <i class="ri-eye-line"></i>
                          詳情
                        </button>
                        <button
                          v-if="coupon.status === 'unused'"
                          class="btn btn-sm btn-danger"
                          @click="handleCancelCoupon(coupon.id)"
                          title="取消優惠券"
                        >
                          <i class="ri-close-circle-line"></i>
                          取消
                        </button>
                        <button 
                          v-if="coupon.status !== 'used' && coupon.status !== 'cancelled' && coupon.status !== 'deleted'"
                          class="btn btn-sm btn-outline-danger" 
                          @click="handleDeleteCoupon(coupon.id)"
                          title="刪除票券（記錄保留，用於處理發放錯誤）"
                        >
                          <i class="ri-delete-bin-line"></i>
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <p>暫無優惠券記錄</p>
            </div>
          </div>
        </div>

        <!-- Wixtar 票券 Tab -->
        <div v-show="activeTab === 'wixtar-tickets'" class="tab-pane">
          <div class="info-section">
            <div class="section-header">
              <h3 class="section-title">Wixtar 票券鏡像</h3>
              <button
                class="btn btn-primary btn-sm"
                :disabled="wixtarTicketsSyncing"
                @click="syncWixtarTickets"
              >
                <i class="ri-refresh-line"></i>
                {{ wixtarTicketsSyncing ? '同步中...' : '重新同步' }}
              </button>
            </div>

            <div class="filter-group" style="margin-bottom: 16px;">
              <select v-model="wixtarTicketsFilter.status" class="form-control" @change="loadWixtarTickets">
                <option value="">所有狀態</option>
                <option value="unused">未使用</option>
                <option value="used">已使用</option>
                <option value="expired">已過期</option>
              </select>
            </div>

            <div v-if="wixtarTicketsLoading" class="loading-state">
              <div class="spinner-sm"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="wixtarTicketsError" class="error-state">
              <p class="error-message">{{ wixtarTicketsError }}</p>
            </div>
            <div v-else-if="wixtarTickets.length > 0" class="table-responsive">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>票券名稱</th>
                    <th>票券編號</th>
                    <th>狀態</th>
                    <th>有效期間</th>
                    <th>核銷時間</th>
                    <th>最後同步</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in wixtarTickets" :key="ticket.id || ticket.wixtarTicketId">
                    <td>{{ ticket.title || '-' }}</td>
                    <td>{{ ticket.wixtarTicketNo || '-' }}</td>
                    <td>
                      <span :class="getCouponStatusBadge(ticket.status)">
                        {{ getWixtarTicketStatusText(ticket.status) }}
                      </span>
                    </td>
                    <td>
                      <span v-if="ticket.validFrom || ticket.validTo">
                        {{ formatDate(ticket.validFrom) }} ~ {{ formatDate(ticket.validTo) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="ticket.usedAt">{{ formatDateTime(ticket.usedAt) }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>{{ formatDateTime(ticket.lastSyncedAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <p>暫無 Wixtar 票券鏡像資料，可點「重新同步」從 Wixtar 拉取</p>
            </div>
          </div>
        </div>

        <!-- 訂單歷史 Tab -->
        <div v-show="activeTab === 'orders'" class="tab-pane">
          <div class="info-section">
            <div class="section-header">
              <h3 class="section-title">訂單歷史</h3>
              <div class="filter-group">
                <select v-model="ordersFilter.status" class="form-control" @change="loadOrders">
                  <option value="">所有狀態</option>
                  <option value="pending">待處理</option>
                  <option value="confirmed">已確認</option>
                  <option value="processing">準備出貨</option>
                  <option value="completed">已下單</option>
                  <option value="shipped">已出貨</option>
                  <option value="cancelled">已取消</option>
                  <option value="refunded">已退款</option>
                </select>
              </div>
            </div>

            <div v-if="ordersLoading" class="loading-state">
              <div class="spinner-sm"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="ordersError" class="error-state">
              <p class="error-message">{{ ordersError }}</p>
            </div>
            <div v-else-if="orders.length > 0" class="table-responsive">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>訂單編號</th>
                    <th>訂單日期</th>
                    <th>訂單金額</th>
                    <th>狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in orders" :key="order.id">
                    <td>{{ order.orderNumber }}</td>
                    <td>{{ formatDateTime(order.orderDate) }}</td>
                    <td>{{ formatCurrency(order.totalAmount) }}</td>
                    <td>
                      <span :class="getOrderStatusBadge(order.status)">
                        {{ getOrderStatusText(order.status) }}
                      </span>
                    </td>
                    <td>
                      <div class="action-buttons">
                        <button 
                          class="btn btn-sm btn-info" 
                          @click="viewOrderDetail(order.id)"
                          title="查看詳情"
                        >
                          <i class="ri-eye-line"></i>
                          詳情
                        </button>
                        <button 
                          v-if="order.status === 'pending' || order.status === 'confirmed'"
                          class="btn btn-sm btn-danger" 
                          @click="handleCancelOrder(order.id, order.orderNumber)"
                          title="取消訂單"
                        >
                          <i class="ri-close-circle-line"></i>
                          取消
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <p>暫無訂單記錄</p>
            </div>
          </div>
        </div>

        <!-- 標籤管理 Tab -->
        <div v-show="activeTab === 'tags'" class="tab-pane">
          <div class="info-section">
            <div class="section-header">
              <h3 class="section-title">會員標籤</h3>
              <button class="btn btn-primary btn-sm" @click="openAddTagModal">
                <i class="ri-add-line"></i>
                新增標籤
              </button>
            </div>

            <div v-if="tagsLoading" class="loading-state">
              <div class="spinner-sm"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="tagsError" class="error-state">
              <p class="error-message">{{ tagsError }}</p>
            </div>
            <div v-else-if="memberTags.length > 0" class="tags-list" style="border: none;background: none;">
              <div v-for="tag in memberTags" :key="tag.id" class="tag-card">
                <div class="tag-info">
                  <i v-if="tag.tagIcon" :class="tag.tagIcon" class="tag-icon"></i>
                  <span class="tag-name" :style="{ color: tag.tagColor }">{{ tag.tagName }}</span>
                  <!-- 顯示標籤濃度（僅互動標籤且濃度 > 1） -->
                  <span 
                    v-if="tag.tagSource === 'manual' && tag.intensity && tag.intensity > 1" 
                    class="tag-intensity"
                    :title="`標籤濃度：${tag.intensity} 次貼標`"
                  >
                    x{{ tag.intensity }}
                  </span>
                  <!-- 標籤類型 badge -->
                  <span v-if="tag.tagSource === 'manual'" class="tag-type-badge manual-type">互動</span>
                  <span v-else-if="tag.tagSource === 'auto_rule'" class="tag-type-badge auto-type">自動</span>
                  <span v-else-if="tag.tagSource === 'system'" class="tag-type-badge system-type">系統</span>
                  <!-- 到期日期 -->
                  <span v-if="tag.expiryDate" class="tag-expiry">
                    {{ formatDate(tag.expiryDate) }} 到期
                  </span>
                </div>
                <button class="btn-remove" @click="removeTag(tag.tagId)" :title="`移除標籤：${tag.tagName}`">
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </div>
            <div v-else class="empty-state">
              <p>暫無標籤</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 調整點數 Modal -->
    <div v-if="showAdjustPointsModal" class="modal-overlay" @click="showAdjustPointsModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>調整點數</h3>
          <button class="modal-close" @click="showAdjustPointsModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="required">調整類型</label>
            <select v-model="adjustPointsForm.type" class="form-control">
              <option value="add">增加點數</option>
              <option value="deduct">扣除點數</option>
            </select>
          </div>
          <div class="form-group">
            <label class="required">點數</label>
            <input 
              v-model.number="adjustPointsForm.amount" 
              type="number" 
              class="form-control" 
              placeholder="請輸入點數"
              min="1"
            />
          </div>
          <div class="form-group">
            <label class="required">說明</label>
            <textarea 
              v-model="adjustPointsForm.reason" 
              class="form-control" 
              rows="3"
              placeholder="請輸入調整原因"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAdjustPointsModal = false">取消</button>
          <button class="btn btn-primary" :disabled="adjustingPoints" @click="submitAdjustPoints">
            {{ adjustingPoints ? '處理中...' : '確定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 發放優惠券 Modal -->
    <div v-if="showIssueCouponModal" class="modal-overlay" @click="showIssueCouponModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>發放優惠券</h3>
          <button class="modal-close" @click="showIssueCouponModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="required">優惠券範本</label>
            <select v-model="issueCouponForm.templateId" class="form-control">
              <option value="">請選擇優惠券範本</option>
              <option v-for="template in couponTemplates" :key="template.id" :value="template.id">
                {{ template.templateName }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="required">數量</label>
            <input 
              v-model.number="issueCouponForm.quantity" 
              type="number" 
              class="form-control" 
              placeholder="請輸入發放數量"
              min="1"
              max="10"
            />
          </div>
          <div class="form-group">
            <label>備註</label>
            <textarea 
              v-model="issueCouponForm.reason" 
              class="form-control" 
              rows="3"
              placeholder="請輸入發放原因（選填）"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showIssueCouponModal = false">取消</button>
          <button class="btn btn-primary" :disabled="issuingCoupon" @click="submitIssueCoupon">
            {{ issuingCoupon ? '處理中...' : '確定發放' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 優惠券詳情 Modal（共用元件）-->
    <CouponDetailModal
      :visible="showCouponDetailModal"
      :coupon="selectedCoupon"
      @close="showCouponDetailModal = false"
      @cancel="(c) => { showCouponDetailModal = false; handleCancelCoupon(c.id); }"
      @delete="(c) => { showCouponDetailModal = false; handleDeleteCoupon(c.id); }"
    />

    <!-- 訂單詳情 Modal -->
    <div v-if="showOrderDetailModal" class="modal-overlay" @click="showOrderDetailModal = false">
      <div class="modal-content modal-lg" @click.stop>
        <div class="modal-header">
          <h3>訂單詳情</h3>
          <button class="modal-close" @click="showOrderDetailModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="orderDetailLoading" class="loading-state">
            <div class="spinner"></div>
            <p>載入中...</p>
          </div>
          <div v-else-if="selectedOrder" class="detail-grid">
            <!-- 基本資訊 -->
            <div class="detail-section">
              <h4 class="section-subtitle">基本資訊</h4>
              <div class="detail-row">
                <span class="detail-label">訂單編號：</span>
                <span class="detail-value code">{{ selectedOrder.orderNumber }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">訂單日期：</span>
                <span class="detail-value">{{ formatDateTime(selectedOrder.orderDate) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">訂單狀態：</span>
                <span class="detail-value">
                  <span :class="getOrderStatusBadge(selectedOrder.status)">
                    {{ getOrderStatusText(selectedOrder.status) }}
                  </span>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">付款狀態：</span>
                <span class="detail-value">{{ selectedOrder.paymentStatus || '-' }}</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.storeName">
                <span class="detail-label">門市名稱：</span>
                <span class="detail-value">{{ selectedOrder.storeName }}</span>
              </div>
            </div>

            <!-- 金額資訊 -->
            <div class="detail-section">
              <h4 class="section-subtitle">金額資訊</h4>
              <div class="detail-row">
                <span class="detail-label">商品小計：</span>
                <span class="detail-value">NT$ {{ selectedOrder.subtotal || 0 }}</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.discountAmount">
                <span class="detail-label">折扣金額：</span>
                <span class="detail-value text-success">- NT$ {{ selectedOrder.discountAmount }}</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.pointsUsed">
                <span class="detail-label">使用點數：</span>
                <span class="detail-value">{{ selectedOrder.pointsUsed }} 點</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.pointsEarned">
                <span class="detail-label">獲得點數：</span>
                <span class="detail-value text-primary">+ {{ selectedOrder.pointsEarned }} 點</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.pointsEarnSummary?.activeEarnPoints > 0">
                <span class="detail-label">可用點數：</span>
                <span class="detail-value text-primary">{{ selectedOrder.pointsEarnSummary.activeEarnPoints }} 點</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.pointsEarnSummary?.pendingEarnPoints > 0">
                <span class="detail-label">待生效點數：</span>
                <span class="detail-value text-warning-strong">{{ selectedOrder.pointsEarnSummary.pendingEarnPoints }} 點</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.pointsEarnSummary?.pendingEffectiveDate">
                <span class="detail-label">生效日期：</span>
                <span class="detail-value">{{ formatEffectiveDate(selectedOrder.pointsEarnSummary.pendingEffectiveDate) }}</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.pointsEarned">
                <span class="detail-label">點數狀態：</span>
                <span class="detail-value">{{ getOrderPointsStatusText(selectedOrder) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">訂單總額：</span>
                <span class="detail-value highlight">NT$ {{ selectedOrder.totalAmount || 0 }}</span>
              </div>
            </div>

            <!-- 備註資訊 -->
            <div class="detail-section" v-if="selectedOrder.remarks || (selectedOrder.status === 'cancelled' && selectedOrder.cancelledAt)">
              <h4 class="section-subtitle">備註資訊</h4>
              <div class="detail-row" v-if="selectedOrder.status === 'cancelled' && selectedOrder.cancelledAt">
                <span class="detail-label">取消時間：</span>
                <span class="detail-value">{{ formatDateTime(selectedOrder.cancelledAt) }}</span>
              </div>
              <div class="detail-row" v-if="selectedOrder.remarks">
                <span class="detail-label">
                  {{ selectedOrder.status === 'cancelled' ? '取消原因：' : '備註：' }}
                </span>
                <span class="detail-value" :class="{ 'cancel-text': selectedOrder.status === 'cancelled' }">
                  {{ selectedOrder.remarks }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showOrderDetailModal = false">關閉</button>
          <button 
            v-if="selectedOrder && (selectedOrder.status === 'pending' || selectedOrder.status === 'confirmed')"
            class="btn btn-danger" 
            @click="handleCancelOrder(selectedOrder.id, selectedOrder.orderNumber)"
          >
            <i class="ri-close-circle-line"></i>
            取消訂單
          </button>
        </div>
      </div>
    </div>

    <!-- 新增標籤 Modal -->
    <div v-if="showAddTagModal" class="modal-overlay" @click="showAddTagModal = false">
      <div class="modal-content modal-md" @click.stop>
        <div class="modal-header">
          <h3>新增標籤</h3>
          <button class="modal-close" @click="showAddTagModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <!-- 已選擇的標籤 -->
          <div v-if="selectedTagIds.length > 0" class="selected-tags-container">
            <label>已選擇 ({{ selectedTagIds.length }})</label>
            <div class="selected-tags">
              <span 
                v-for="tagId in selectedTagIds" 
                :key="tagId"
                class="selected-tag-item"
              >
                <i :class="getTagIcon(tagId)" :style="{ color: getTagColor(tagId) }"></i>
                {{ getTagName(tagId) }}
                <button 
                  type="button"
                  class="remove-tag-btn"
                  @click="removeFromSelection(tagId)"
                >
                  <i class="ri-close-line"></i>
                </button>
              </span>
            </div>
          </div>

          <!-- 篩選框 -->
          <div class="form-group">
            <label>篩選標籤</label>
            <input 
              v-model="tagSearchQuery"
              type="text"
              class="form-control"
              placeholder="輸入標籤名稱篩選..."
              @input="filterTags"
            />
          </div>

          <!-- 標籤來源頁籤 -->
          <div class="tag-tabs">
            <button 
              class="tag-tab"
              :class="{ active: activeTagTab === 'all' }"
              @click="activeTagTab = 'all'; filterTags()"
            >
              <i class="ri-list-check"></i>
              全部標籤
            </button>
            <button 
              class="tag-tab"
              :class="{ active: activeTagTab === 'manual' }"
              @click="activeTagTab = 'manual'; filterTags()"
            >
              <i class="ri-hand-heart-line"></i>
              互動標籤
            </button>
            <button 
              class="tag-tab"
              :class="{ active: activeTagTab === 'auto_rule' }"
              @click="activeTagTab = 'auto_rule'; filterTags()"
            >
              <i class="ri-robot-line"></i>
              自動標籤
            </button>
          </div>

          <!-- 可選標籤列表 -->
          <div class="form-group">
            <label>可選標籤 ({{ filteredAvailableTags.length }})</label>
            <p class="form-hint" style="margin-bottom: 12px; color: #666;">
              <i class="ri-information-line"></i> 
              互動標籤可重複貼標，用於累積標籤濃度
            </p>
            <div class="tags-list">
              <div 
                v-for="tag in filteredAvailableTags" 
                :key="tag.id"
                class="tag-list-item"
                :class="{ 
                  'selected': selectedTagIds.includes(tag.id),
                  'disabled': isTagAlreadyAdded(tag.id),
                  'manual-tag': tag.tagSource === 'manual'
                }"
                @click="toggleTagSelection(tag.id)"
              >
                <div class="tag-info">
                  <i :class="tag.tagIcon" :style="{ color: tag.tagColor }"></i>
                  <span class="tag-name">{{ tag.tagName }}</span>
                  <span v-if="tag.tagSource === 'manual'" class="tag-badge manual-badge">互動</span>
                  <span v-else-if="tag.tagSource === 'auto_rule'" class="tag-badge auto-badge">自動</span>
                  <span v-if="tag.isTemporary" class="tag-badge temp-badge">限時</span>
                  <span v-if="isTagAlreadyAdded(tag.id)" class="tag-badge added-badge">已添加</span>
                </div>
                <div v-if="selectedTagIds.includes(tag.id)" class="check-icon">
                  <i class="ri-check-line"></i>
                </div>
              </div>
              <div v-if="filteredAvailableTags.length === 0" class="empty-state">
                <p>{{ tagSearchQuery ? '找不到符合的標籤' : '沒有可用標籤' }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAddTagModal = false">取消</button>
          <button 
            class="btn btn-primary" 
            :disabled="addingTag || selectedTagIds.length === 0" 
            @click="addMultipleTags"
          >
            {{ addingTag ? '處理中...' : `確定新增 (${selectedTagIds.length})` }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { memberService } from '@/api/modules/members.js';
import { pointsService } from '@/api/modules/points.js';
import { couponService } from '@/api/modules/coupons.js';
import { orderService } from '@/api/modules/orders.js';
import { tagService } from '@/api/modules/tags.js';
import { couponTemplateService } from '@/api/modules/couponTemplates.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';
import Swal from 'sweetalert2';
import CouponDetailModal from '@/components/CouponDetailModal.vue';

export default {
  name: 'MemberDetail',
  components: { CouponDetailModal },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // Basic states
    const member = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const activeTab = ref('basic');

    // Points states
    const pointsTransactions = ref([]);
    const pointsBalance = ref(null);
    const pointsLoading = ref(false);
    const pointsError = ref(null);
    const pointsFilter = ref({ type: '' });
    const showAdjustPointsModal = ref(false);
    const adjustPointsForm = ref({ type: 'add', amount: null, reason: '' });
    const adjustingPoints = ref(false);

    // Coupons states
    const coupons = ref([]);
    const couponsLoading = ref(false);
    const couponsError = ref(null);
    const couponsFilter = ref({ status: '' });
    const showIssueCouponModal = ref(false);
    const issueCouponForm = ref({ templateId: '', quantity: 1, reason: '' });
    const issuingCoupon = ref(false);
    const couponTemplates = ref([]);
    const showCouponDetailModal = ref(false);
    const selectedCoupon = ref(null);

    // Orders states
    const orders = ref([]);
    const ordersLoading = ref(false);
    const ordersError = ref(null);
    const ordersFilter = ref({ status: '' });
    const showOrderDetailModal = ref(false);
    const selectedOrder = ref(null);
    const orderDetailLoading = ref(false);

    // Tags states
    const memberTags = ref([]);
    const tagsLoading = ref(false);
    const tagsError = ref(null);
    const showAddTagModal = ref(false);
    const selectedTagIds = ref([]); // 改為陣列
    const addingTag = ref(false);
    const availableTags = ref([]);
    const filteredAvailableTags = ref([]);
    const tagSearchQuery = ref('');
    const activeTagTab = ref('all'); // 新增：當前標籤頁籤

    // Wixtar ticket mirror
    const isWixtarClient = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const wixtarTicketsError = ref(null);
    const wixtarTicketsFilter = ref({ status: '' });
    const wixtarTicketsSyncing = ref(false);

    const tabs = computed(() => {
      const base = [
        { id: 'basic', label: '基本資訊', icon: 'ri-user-line' },
        { id: 'points', label: '點數管理', icon: 'ri-coin-line' },
        { id: 'coupons', label: '優惠券', icon: 'ri-ticket-line' }
      ];
      if (isWixtarClient.value) {
        base.push({ id: 'wixtar-tickets', label: 'Wixtar 票券', icon: 'ri-coupon-3-line' });
      }
      base.push(
        { id: 'orders', label: '訂單歷史', icon: 'ri-shopping-bag-line' },
        { id: 'tags', label: '標籤管理', icon: 'ri-price-tag-3-line' }
      );
      return base;
    });

    const pageTitle = computed(() => {
      return member.value ? `會員詳情 - ${member.value.name}` : '會員詳情';
    });

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.success && res.data?.clientData?.memberSystemType === 'WIXTAR';
      } catch (e) {
        console.error(e);
        isWixtarClient.value = false;
      }
    };

    const loadWixtarTickets = async () => {
      if (!isWixtarClient.value) return;
      wixtarTicketsLoading.value = true;
      wixtarTicketsError.value = null;
      try {
        const params = {};
        if (wixtarTicketsFilter.value.status) {
          params.status = wixtarTicketsFilter.value.status;
        }
        const response = await wixtarService.getMemberTicketMirror(route.params.id, params);
        if (response && response.success && response.data) {
          wixtarTickets.value = response.data.items || [];
        } else {
          wixtarTicketsError.value = response?.message || '獲取 Wixtar 票券鏡像失敗';
        }
      } catch (err) {
        console.error('獲取 Wixtar 票券鏡像失敗:', err);
        wixtarTicketsError.value = err.message || '獲取 Wixtar 票券鏡像失敗';
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    const syncWixtarTickets = async () => {
      wixtarTicketsSyncing.value = true;
      try {
        const response = await wixtarService.syncMemberTicketMirror(route.params.id);
        if (response && response.success) {
          const fetched = response.data?.fetched ?? 0;
          await showSuccess('同步完成', `已更新 ${fetched} 筆票券鏡像`);
          await loadWixtarTickets();
        } else {
          await showError('同步失敗', response?.message || '同步 Wixtar 票券失敗');
        }
      } catch (err) {
        console.error('同步 Wixtar 票券失敗:', err);
        await showError('同步失敗', err.message || '同步 Wixtar 票券失敗');
      } finally {
        wixtarTicketsSyncing.value = false;
      }
    };

    const getWixtarTicketStatusText = (status) => {
      const map = { unused: '未使用', used: '已使用', expired: '已過期' };
      return map[status] || status || '-';
    };

    const loadMember = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await memberService.getMember(route.params.id);

        if (response && response.success && response.data) {
          member.value = response.data;
        } else {
          error.value = response?.message || '獲取會員詳情失敗';
          await showError('載入失敗', error.value);
        }
      } catch (err) {
        console.error('獲取會員詳情失敗:', err);
        error.value = err.message || '獲取會員詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const loadPointsTransactions = async () => {
      pointsLoading.value = true;
      pointsError.value = null;

      try {
        const params = {
          memberId: route.params.id,
          page: 1,
          pageSize: 50
        };
        if (pointsFilter.value.type) {
          params.transactionType = pointsFilter.value.type;
        }

        const [txRes, balanceRes] = await Promise.all([
          pointsService.getTransactions(route.params.id, params),
          pointsService.getBalance(route.params.id),
        ]);

        if (txRes && txRes.success && txRes.data) {
          pointsTransactions.value = txRes.data.items || [];
        } else {
          pointsError.value = '獲取點數交易記錄失敗';
        }

        if (balanceRes && balanceRes.success && balanceRes.data) {
          pointsBalance.value = balanceRes.data;
        }
      } catch (err) {
        console.error('獲取點數交易記錄失敗:', err);
        pointsError.value = err.message || '獲取點數交易記錄失敗';
      } finally {
        pointsLoading.value = false;
      }
    };

    const loadCoupons = async () => {
      couponsLoading.value = true;
      couponsError.value = null;

      try {
        const params = {
          page: 1,
          pageSize: 50
        };
        if (couponsFilter.value.status) {
          params.status = couponsFilter.value.status;
        }

        const response = await couponService.getCouponsByMember(route.params.id, params);
        
        if (response && response.success && response.data) {
          coupons.value = response.data.items || [];
        } else {
          couponsError.value = '獲取優惠券列表失敗';
        }
      } catch (err) {
        console.error('獲取優惠券列表失敗:', err);
        couponsError.value = err.message || '獲取優惠券列表失敗';
      } finally {
        couponsLoading.value = false;
      }
    };

    // 查看優惠券詳情
    const viewCouponDetail = (coupon) => {
      selectedCoupon.value = coupon;
      showCouponDetailModal.value = true;
    };

    const handleCancelCoupon = async (couponId) => {
      try {
        const result = await Swal.fire({
          title: '確定要取消這張優惠券嗎？',
          text: '取消後無法恢復',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#6c757d',
          confirmButtonText: '確定取消',
          cancelButtonText: '返回',
          input: 'textarea',
          inputLabel: '取消原因（選填）',
          inputPlaceholder: '請輸入取消原因...',
          inputAttributes: {
            'aria-label': '取消原因'
          }
        });

        if (result.isConfirmed) {
          const response = await couponService.cancelCoupon(couponId, result.value || '');

          if (response && response.success) {
            await showSuccess('成功', '優惠券已取消');
            showCouponDetailModal.value = false; // 關閉詳情 Modal
            await loadMember();
            await loadCoupons();
          } else {
            await showError('取消失敗', response?.message || '取消優惠券失敗');
          }
        }
      } catch (err) {
        console.error('取消優惠券失敗:', err);
        await showError('取消失敗', err.message || '取消優惠券失敗');
      }
    };

    const handleDeleteCoupon = async (couponId) => {
      try {
        const result = await Swal.fire({
          title: '確定要永久刪除這張票券嗎？',
          html: '<p>此操作無法復原，票券記錄將從資料庫中移除。</p><p style="color:#d33;font-weight:600;">僅限處理發放錯誤時使用。</p>',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#6c757d',
          confirmButtonText: '確定刪除',
          cancelButtonText: '返回'
        });

        if (result.isConfirmed) {
          const response = await couponService.deleteCouponIssue(couponId);

          if (response && response.success) {
            await showSuccess('成功', '票券已永久刪除');
            showCouponDetailModal.value = false;
            await loadMember();
            await loadCoupons();
          } else {
            await showError('刪除失敗', response?.message || '刪除票券失敗');
          }
        }
      } catch (err) {
        console.error('刪除票券失敗:', err);
        await showError('刪除失敗', err.response?.data?.message || err.message || '刪除票券失敗');
      }
    };

    const loadOrders = async () => {
      ordersLoading.value = true;
      ordersError.value = null;

      try {
        const params = {
          memberId: route.params.id,
          page: 1,
          pageSize: 50
        };
        if (ordersFilter.value.status) {
          params.status = ordersFilter.value.status;
        }

        const response = await orderService.getOrders(params);
        
        if (response && response.success && response.data) {
          orders.value = response.data.items || [];
        } else {
          ordersError.value = '獲取訂單列表失敗';
        }
      } catch (err) {
        console.error('獲取訂單列表失敗:', err);
        ordersError.value = err.message || '獲取訂單列表失敗';
      } finally {
        ordersLoading.value = false;
      }
    };

    const loadMemberTags = async () => {
      tagsLoading.value = true;
      tagsError.value = null;

      try {
        const response = await tagService.getMemberTags(route.params.id);
        
        if (response && response.success && response.data) {
          memberTags.value = response.data || [];
        } else {
          tagsError.value = '獲取標籤列表失敗';
        }
      } catch (err) {
        console.error('獲取標籤列表失敗:', err);
        tagsError.value = err.message || '獲取標籤列表失敗';
      } finally {
        tagsLoading.value = false;
      }
    };

    const loadCouponTemplates = async () => {
      try {
        const response = await couponTemplateService.getCouponTemplates({ page: 1, pageSize: 100 });
        if (response && response.success && response.data) {
          couponTemplates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取優惠券範本失敗:', err);
      }
    };

    const loadAvailableTags = async () => {
      try {
        const response = await tagService.getTags({ isActive: true });
        if (response && response.success && response.data) {
          // 後端返回的 data 直接是陣列，不是分頁物件
          availableTags.value = (Array.isArray(response.data) ? response.data : [])
            .filter(tag => tag.tagSource !== 'system');
          filteredAvailableTags.value = availableTags.value; // 初始化過濾列表
          console.log('載入標籤成功:', availableTags.value.length, '個標籤');
        }
      } catch (err) {
        console.error('獲取標籤列表失敗:', err);
      }
    };

    // 過濾標籤
    const filterTags = () => {
      const query = tagSearchQuery.value.toLowerCase().trim();
      let tags = availableTags.value;
      
      // 根據頁籤篩選
      if (activeTagTab.value !== 'all') {
        tags = tags.filter(tag => tag.tagSource === activeTagTab.value);
      }
      
      // 根據搜尋關鍵字篩選
      if (query) {
        tags = tags.filter(tag => 
          tag.tagName.toLowerCase().includes(query) || 
          (tag.description && tag.description.toLowerCase().includes(query))
        );
      }
      
      filteredAvailableTags.value = tags;
    };

    // 切換標籤選擇
    const toggleTagSelection = (tagId) => {
      // 檢查標籤類型
      const tag = availableTags.value.find(t => t.id === tagId);
      if (!tag) return;
      
      // 自動規則標籤已經添加到會員時，不允許重複選擇
      if (tag.tagSource !== 'manual' && isTagAlreadyAdded(tagId)) {
        return;
      }
      
      // 互動標籤（manual）允許重複選擇，以支援標籤濃度累積

      const index = selectedTagIds.value.indexOf(tagId);
      if (index > -1) {
        // 已選中，移除
        selectedTagIds.value.splice(index, 1);
      } else {
        // 未選中，添加
        selectedTagIds.value.push(tagId);
      }
    };

    // 從選擇中移除
    const removeFromSelection = (tagId) => {
      const index = selectedTagIds.value.indexOf(tagId);
      if (index > -1) {
        selectedTagIds.value.splice(index, 1);
      }
    };

    // 獲取標籤名稱
    const getTagName = (tagId) => {
      const tag = availableTags.value.find(t => t.id === tagId);
      return tag ? tag.tagName : '';
    };

    // 獲取標籤圖示
    const getTagIcon = (tagId) => {
      const tag = availableTags.value.find(t => t.id === tagId);
      return tag ? tag.tagIcon : 'ri-price-tag-3-line';
    };

    // 獲取標籤顏色
    const getTagColor = (tagId) => {
      const tag = availableTags.value.find(t => t.id === tagId);
      return tag ? tag.tagColor : '#000000';
    };

    const submitAdjustPoints = async () => {
      if (!adjustPointsForm.value.amount || !adjustPointsForm.value.reason) {
        await showError('驗證失敗', '請填寫完整資訊');
        return;
      }

      adjustingPoints.value = true;

      try {
        const data = {
          memberId: route.params.id,
          points: Number(adjustPointsForm.value.amount),
          reason: adjustPointsForm.value.reason.trim()
        };

        let response;
        if (adjustPointsForm.value.type === 'add') {
          response = await pointsService.addPoints(data);
        } else {
          response = await pointsService.deductPoints(data);
        }

        if (response && response.success) {
          await showSuccess('成功', '點數調整成功');
          showAdjustPointsModal.value = false;
          adjustPointsForm.value = { type: 'add', amount: null, reason: '' };
          await loadMember();
          await loadPointsTransactions();
        } else {
          await showError('調整失敗', response?.message || '點數調整失敗');
        }
      } catch (err) {
        console.error('調整點數失敗:', err);
        await showError('調整失敗', err.message || '點數調整失敗');
      } finally {
        adjustingPoints.value = false;
      }
    };

    const submitIssueCoupon = async () => {
      if (!issueCouponForm.value.templateId || !issueCouponForm.value.quantity) {
        await showError('驗證失敗', '請填寫完整資訊');
        return;
      }

      issuingCoupon.value = true;

      try {
        const response = await couponService.issueCoupon({
          memberId: route.params.id,
          templateId: issueCouponForm.value.templateId,
          reason: issueCouponForm.value.reason || '手動發放',
          quantity: issueCouponForm.value.quantity
        });

        if (response && response.success) {
          await showSuccess('成功', '優惠券發放成功');
          showIssueCouponModal.value = false;
          issueCouponForm.value = { templateId: '', quantity: 1, reason: '' };
          await loadMember();
          await loadCoupons();
        } else {
          await showError('發放失敗', response?.message || '優惠券發放失敗');
        }
      } catch (err) {
        console.error('發放優惠券失敗:', err);
        await showError('發放失敗', err.message || '優惠券發放失敗');
      } finally {
        issuingCoupon.value = false;
      }
    };

    const openAddTagModal = async () => {
      showAddTagModal.value = true;
      tagSearchQuery.value = ''; // 重置篩選條件
      selectedTagIds.value = []; // 重置選擇
      await loadAvailableTags();
    };

    const isTagAlreadyAdded = (tagId) => {
      // 檢查標籤是否已添加，並且是否為非互動標籤
      const tag = availableTags.value.find(t => t.id === tagId);
      if (!tag) return false;
      
      // 只有非互動標籤（自動規則標籤、專屬標籤）才禁止重複貼
      // 互動標籤（manual）允許重複貼，以累積標籤濃度
      if (tag.tagSource === 'manual') {
        return false; // 互動標籤不顯示「已添加」，允許重複貼
      }
      
      // 自動規則標籤與專屬標籤才檢查是否已添加
      return memberTags.value.some(memberTag => memberTag.tagId === tagId);
    };

    const addMultipleTags = async () => {
      if (selectedTagIds.value.length === 0) {
        await showError('驗證失敗', '請至少選擇一個標籤');
        return;
      }

      addingTag.value = true;

      try {
        let successCount = 0;
        let failCount = 0;
        const errors = [];

        // 逐個新增標籤
        for (const tagId of selectedTagIds.value) {
          try {
            const response = await tagService.addTagToMember(route.params.id, tagId);
            if (response && response.success) {
              successCount++;
            } else {
              failCount++;
              const tagName = getTagName(tagId);
              errors.push(`${tagName}: ${response?.message || '新增失敗'}`);
            }
          } catch (err) {
            failCount++;
            const tagName = getTagName(tagId);
            errors.push(`${tagName}: ${err.message || '新增失敗'}`);
          }
        }

        // 顯示結果
        if (successCount > 0 && failCount === 0) {
          await showSuccess('成功', `已成功新增 ${successCount} 個標籤`);
          showAddTagModal.value = false;
          selectedTagIds.value = [];
          tagSearchQuery.value = '';
          await loadMemberTags();
        } else if (successCount > 0 && failCount > 0) {
          await Swal.fire({
            icon: 'warning',
            title: '部分成功',
            html: `
              <p>成功新增 ${successCount} 個標籤</p>
              <p>失敗 ${failCount} 個標籤</p>
              <div style="text-align: left; margin-top: 10px;">
                ${errors.map(err => `<div>• ${err}</div>`).join('')}
              </div>
            `,
            confirmButtonText: '確定'
          });
          showAddTagModal.value = false;
          selectedTagIds.value = [];
          tagSearchQuery.value = '';
          await loadMemberTags();
        } else {
          await Swal.fire({
            icon: 'error',
            title: '新增失敗',
            html: `
              <div style="text-align: left;">
                ${errors.map(err => `<div>• ${err}</div>`).join('')}
              </div>
            `,
            confirmButtonText: '確定'
          });
        }
      } catch (err) {
        console.error('批次新增標籤失敗:', err);
        await showError('新增失敗', err.message || '批次新增標籤失敗');
      } finally {
        addingTag.value = false;
      }
    };

    // 保留原有的 addTag 方法作為單一標籤新增（供其他地方使用）
    const addTag = async () => {
      if (!selectedTagIds.value || selectedTagIds.value.length === 0) {
        await showError('驗證失敗', '請選擇標籤');
        return;
      }
      // 如果只有一個標籤，直接呼叫舊邏輯
      if (selectedTagIds.value.length === 1) {
        addingTag.value = true;
        try {
          const response = await tagService.addTagToMember(route.params.id, selectedTagIds.value[0]);
          if (response && response.success) {
            await showSuccess('成功', '標籤新增成功');
            showAddTagModal.value = false;
            selectedTagIds.value = [];
            tagSearchQuery.value = '';
            await loadMemberTags();
          } else {
            await showError('新增失敗', response?.message || '標籤新增失敗');
          }
        } catch (err) {
          console.error('新增標籤失敗:', err);
          await showError('新增失敗', err.message || '標籤新增失敗');
        } finally {
          addingTag.value = false;
        }
      } else {
        // 多個標籤，呼叫批次新增
        await addMultipleTags();
      }
    };

    const removeTag = async (tagId) => {
      try {
        const response = await tagService.removeTagFromMember(route.params.id, tagId);

        if (response && response.success) {
          await showSuccess('成功', '標籤移除成功');
          await loadMemberTags();
        } else {
          await showError('移除失敗', response?.message || '標籤移除失敗');
        }
      } catch (err) {
        console.error('移除標籤失敗:', err);
        await showError('移除失敗', err.message || '標籤移除失敗');
      }
    };

    const viewOrderDetail = async (orderId) => {
      orderDetailLoading.value = true;
      showOrderDetailModal.value = true;
      selectedOrder.value = null;

      try {
        const response = await orderService.getOrder(orderId);
        if (response && response.success) {
          selectedOrder.value = response.data;
        } else {
          await showError('載入失敗', response?.message || '無法載入訂單詳情');
          showOrderDetailModal.value = false;
        }
      } catch (err) {
        console.error('載入訂單詳情失敗:', err);
        await showError('載入失敗', err.message || '無法載入訂單詳情');
        showOrderDetailModal.value = false;
      } finally {
        orderDetailLoading.value = false;
      }
    };

    // 取消訂單
    const handleCancelOrder = async (orderId, orderNumber) => {
      try {
        const result = await Swal.fire({
          title: '確定要取消訂單嗎？',
          html: `訂單編號：<strong>${orderNumber}</strong><br/>取消後無法恢復`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#6c757d',
          confirmButtonText: '確定取消',
          cancelButtonText: '返回',
          input: 'textarea',
          inputLabel: '取消原因（選填）',
          inputPlaceholder: '請輸入取消原因...',
          inputAttributes: {
            'aria-label': '取消原因'
          }
        });

        if (result.isConfirmed) {
          const response = await orderService.updateOrderStatus(orderId, {
            status: 'cancelled',
            remarks: result.value || '客戶取消訂單'
          });

          if (response && response.success) {
            await showSuccess('成功', '訂單已取消');
            showOrderDetailModal.value = false; // 如果詳情 Modal 開啟，則關閉
            await loadOrders(); // 重新載入訂單列表
          } else {
            await showError('取消失敗', response?.message || '取消訂單失敗');
          }
        }
      } catch (err) {
        console.error('取消訂單失敗:', err);
        await showError('取消失敗', err.message || '取消訂單失敗');
      }
    };

    const handleBack = () => {
      router.push({ name: 'ClientMembers' });
    };

    const handleEdit = () => {
      router.push({ name: 'EditMember', params: { id: route.params.id } });
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const formatDateTime = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatEffectiveDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };

    const getOrderPointsStatusText = (order) => {
      const summary = order?.pointsEarnSummary;
      if (!order?.pointsEarned || order.pointsEarned <= 0) return '-';
      if (order.pointsStatus === 'pending' && !summary) return '待發放';
      if (summary?.pendingEarnPoints > 0 && summary?.activeEarnPoints > 0) {
        return '部分可用、部分待生效';
      }
      if (summary?.pendingEarnPoints > 0) return '已發放（待生效）';
      if (summary?.activeEarnPoints > 0 || order.pointsStatus === 'earned') return '已發放（可用）';
      return '待發放';
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return 'NT$ 0';
      return `NT$ ${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return Number(num).toLocaleString('zh-TW');
    };

    const formatCouponValue = (type, value) => {
      if (type === 'fixed') {
        return formatCurrency(value);
      } else if (type === 'percentage') {
        return `${value}%`;
      }
      return '-';
    };

    const getGenderText = (gender) => {
      const genderMap = {
        male: '男性',
        female: '女性',
        other: '其他',
        prefer_not_to_say: '不願透露'
      };
      return genderMap[gender] || '-';
    };

    const getStatusText = (status) => {
      const statusMap = {
        active: '正常',
        inactive: '停用',
        suspended: '暫停',
        deleted: '已刪除'
      };
      return statusMap[status] || status || '-';
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        active: 'badge badge-success',
        inactive: 'badge badge-secondary',
        suspended: 'badge badge-warning',
        deleted: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getRegistrationMethodText = (method) => {
      const methodMap = {
        website: '網站註冊',
        app: 'APP註冊',
        admin: '後台建立',
        import: '批次匯入',
        line: 'LINE註冊',
        facebook: 'Facebook註冊',
        google: 'Google註冊'
      };
      return methodMap[method] || '-';
    };

    const getFullAddress = (member) => {
      const parts = [];
      if (member.addressCity) parts.push(member.addressCity);
      if (member.addressDistrict) parts.push(member.addressDistrict);
      if (member.addressStreet) parts.push(member.addressStreet);
      if (member.addressPostalCode) parts.push(`(${member.addressPostalCode})`);
      return parts.join('');
    };

    const getTransactionTypeText = (type) => {
      const typeMap = {
        earn: '獲得',
        redeem: '兌換',
        expire: '過期',
        adjust: '調整'
      };
      return typeMap[type] || type;
    };

    const getTransactionStatusText = (status, effectiveDate = null) => {
      const statusMap = {
        pending: '待生效',
        active: '可用',
        expired: '已過期',
        used: '已使用',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      const base = statusMap[status] || status;
      if (status === 'pending' && effectiveDate) {
        return `${base}（${formatEffectiveDate(effectiveDate)}）`;
      }
      return base;
    };

    const getTransactionStatusBadge = (status) => {
      const badgeMap = {
        pending: 'badge badge-warning',
        active: 'badge badge-success',
        expired: 'badge badge-danger',
        used: 'badge badge-secondary',
        // 以下為相容舊資料或其他狀態
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-secondary'
      };
      return badgeMap[status] || 'badge badge-secondary';
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        fixed: '固定金額',
        percentage: '百分比折扣',
        freebie: '贈品'
      };
      return typeMap[type] || type;
    };

    const getCouponStatusText = (status) => {
      const statusMap = {
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消',
        deleted: '已刪除'
      };
      return statusMap[status] || status;
    };

    const getCouponStatusBadge = (status) => {
      const badgeMap = {
        unused: 'badge badge-success',
        transferring: 'badge badge-warning',
        transferred_out: 'badge badge-info',
        used: 'badge badge-secondary',
        expired: 'badge badge-danger',
        cancelled: 'badge badge-warning',
        deleted: 'badge badge-danger'
      };
      return badgeMap[status] || 'badge badge-secondary';
    };

    // 優惠券發放來源平台
    const getCouponIssueSourceText = (source) => {
      const sourceMap = {
        crm_admin: 'CRM管理後台',
        pos_system: 'POS系統',
        mobile_app: '手機APP',
        external_api: '外部API',
        partner_platform: '合作夥伴平台',
        line_bot: 'LINE機器人',
        web_portal: '會員網站'
      };
      return sourceMap[source] || source || '-';
    };

    // 優惠券發放方式
    const getCouponIssueMethodText = (method) => {
      const methodMap = {
        manual: '手動發放',
        batch: '批次發放',
        order: '訂單贈送',
        activity: '活動發放',
        promotion: '促銷活動',
        batch_operation: '批次發放',
        sign_in: '簽到獎勵',
        referral: '推薦獎勵',
        birthday: '生日禮',
        register: '註冊贈送',
        system: '系統發放'
      };
      return methodMap[method] || method || '-';
    };

    const getOrderStatusText = (status) => {
      const statusMap = {
        pending: '待處理',
        confirmed: '已確認',
        processing: '準備出貨',
        completed: '已下單',
        shipped: '已出貨',
        delivered: '已到店',
        cancelled: '已取消',
        refunded: '已退款'
      };
      return statusMap[status] || status;
    };

    const getOrderStatusBadge = (status) => {
      const badgeMap = {
        pending: 'badge badge-warning',
        confirmed: 'badge badge-info',
        processing: 'badge badge-primary',
        shipped: 'badge badge-info',
        delivered: 'badge badge-primary',
        completed: 'badge badge-success',
        cancelled: 'badge badge-danger',
        refunded: 'badge badge-secondary'
      };
      return badgeMap[status] || 'badge badge-secondary';
    };

    onMounted(async () => {
      await loadClientMemberSystem();
      await loadMember();
      await loadPointsTransactions();
      await loadCoupons();
      await loadOrders();
      await loadMemberTags();
      await loadCouponTemplates();
      if (isWixtarClient.value) {
        await loadWixtarTickets();
      }
    });

    watch(activeTab, async (tab) => {
      if (tab === 'points') {
        await Promise.all([loadMember(), loadPointsTransactions()]);
      }
      if (tab === 'wixtar-tickets') {
        await loadWixtarTickets();
      }
    });

    return {
      member,
      loading,
      error,
      activeTab,
      tabs,
      pageTitle,
      // Points
      pointsTransactions,
      pointsBalance,
      pointsLoading,
      pointsError,
      pointsFilter,
      showAdjustPointsModal,
      adjustPointsForm,
      adjustingPoints,
      loadPointsTransactions,
      submitAdjustPoints,
      // Coupons
      coupons,
      couponsLoading,
      couponsError,
      couponsFilter,
      showIssueCouponModal,
      issueCouponForm,
      issuingCoupon,
      couponTemplates,
      showCouponDetailModal,
      selectedCoupon,
      viewCouponDetail,
      loadCoupons,
      submitIssueCoupon,
      handleCancelCoupon,
      handleDeleteCoupon,
      // Wixtar tickets
      isWixtarClient,
      wixtarTickets,
      wixtarTicketsLoading,
      wixtarTicketsError,
      wixtarTicketsFilter,
      wixtarTicketsSyncing,
      loadWixtarTickets,
      syncWixtarTickets,
      getWixtarTicketStatusText,
      // Orders
      orders,
      ordersLoading,
      ordersError,
      ordersFilter,
      showOrderDetailModal,
      selectedOrder,
      orderDetailLoading,
      loadOrders,
      viewOrderDetail,
      handleCancelOrder,
      // Tags
      memberTags,
      tagsLoading,
      tagsError,
      showAddTagModal,
      addingTag,
      availableTags,
      filteredAvailableTags,
      tagSearchQuery,
      selectedTagIds,
      activeTagTab,
      openAddTagModal,
      isTagAlreadyAdded,
      addTag,
      addMultipleTags,
      removeTag,
      filterTags,
      toggleTagSelection,
      removeFromSelection,
      getTagName,
      getTagIcon,
      getTagColor,
      // Methods
      loadMember,
      handleBack,
      handleEdit,
      formatDate,
      formatDateTime,
      formatEffectiveDate,
      getOrderPointsStatusText,
      formatCurrency,
      formatNumber,
      formatCouponValue,
      getGenderText,
      getStatusText,
      getStatusBadgeClass,
      getRegistrationMethodText,
      getFullAddress,
      getTransactionTypeText,
      getTransactionStatusText,
      getTransactionStatusBadge,
      getCouponTypeText,
      getCouponStatusText,
      getCouponStatusBadge,
      getCouponIssueSourceText,
      getCouponIssueMethodText,
      getOrderStatusText,
      getOrderStatusBadge
    };
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.header-actions .btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.header-actions .btn-primary { background: #3b82f6; color: #fff; }
.header-actions .btn-primary:hover { background: #2563eb; }
.header-actions .btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.header-actions .btn-secondary:hover { background: #e5e7eb; }

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.spinner-sm {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc3545;
  font-size: 16px;
  margin-bottom: 20px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Tabs Navigation */
.tabs-nav {
  display: flex;
  background: white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}

.tab-btn {
  flex: 1;
  padding: 16px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.tab-btn:hover {
  background: #f8f9fa;
  color: #333;
}

.tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
  background: #f8f9ff;
}

.tab-btn i {
  font-size: 18px;
}

/* Tabs Content */
.tabs-content {
  background: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 400px;
}

.tab-pane {
  padding: 24px;
}

.info-section {
  margin-bottom: 24px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-weight: 500;
  color: #666;
  min-width: 120px;
}

.info-value {
  color: #333;
}

.info-value.highlight {
  color: #667eea;
  font-weight: 600;
  font-size: 16px;
}

/* Filter Group */
.filter-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

/* Table */
.table-responsive {
  overflow-x: auto;
  margin-top: 16px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.data-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.data-table td {
  font-size: 14px;
  color: #666;
}

.data-table tbody tr:hover {
  background: #f8f9fa;
}

.text-success {
  color: #28a745;
  font-weight: 600;
}

.text-danger {
  color: #dc3545;
  font-weight: 600;
}

.text-warning-strong {
  color: #d97706;
  font-weight: 600;
}

.points-stats-grid {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

/* Tags List */
.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.tag-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  min-width: 200px;
}

.tag-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.tag-icon {
  font-size: 18px;
}

.tag-name {
  font-weight: 600;
  font-size: 14px;
}

.tag-expiry {
  font-size: 12px;
  color: #f57c00;
  padding: 2px 6px;
  background: #fff9c4;
  border-radius: 4px;
}

/* 標籤濃度顯示 */
.tag-intensity {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 22px;
  padding: 0 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 12px;
  font-weight: 700;
  border-radius: 11px;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  cursor: help;
  transition: all 0.2s;
}

.tag-intensity:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 6px rgba(102, 126, 234, 0.4);
}

/* 標籤類型 badge */
.tag-type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.manual-type {
  background-color: #fff3e0;
  color: #e65100;
}

.auto-type {
  background-color: #e3f2fd;
  color: #1565c0;
}

.system-type {
  background-color: #f3e5f5;
  color: #6a1b9a;
}

.btn-remove {
  background: transparent;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #fee;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background-color: #667eea;
  color: white;
}

.btn-primary:hover {
  background-color: #5568d3;
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

/* Badges */
.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda;
  color: #155724;
}

.badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}

.badge-info {
  background-color: #d1ecf1;
  color: #0c5460;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
}

.badge-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.badge-primary {
  background-color: #cfe2ff;
  color: #084298;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f8f9fa;
  color: #666;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-group textarea {
  resize: vertical;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* 發放原因欄位樣式 */
.issue-reason {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
}

.issue-reason.cancel-reason {
  color: #e74c3c;
  font-style: italic;
}

/* 優惠券詳情 Modal */
.modal-lg {
  max-width: 800px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.detail-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #667eea;
}

.detail-row {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  flex: 0 0 120px;
  font-weight: 500;
  color: #6c757d;
}

.detail-value {
  flex: 1;
  color: #2c3e50;
}

.detail-value.code {
  font-family: 'Courier New', monospace;
  background: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.detail-value.highlight {
  color: #667eea;
  font-weight: 600;
  font-size: 18px;
}

.detail-value.cancel-text {
  color: #e74c3c;
  font-style: italic;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-info {
  background: #17a2b8;
  color: white;
  border: none;
}

.btn-info:hover {
  background: #138496;
}

.cancel-reason-inline {
  font-size: 12px;
  color: #888;
  margin-top: 3px;
  max-width: 180px;
  white-space: normal;
  word-break: break-all;
}

.btn-outline-danger {
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: #fff;
}

/* 標籤選擇 Modal 樣式 */
.modal-md {
  max-width: 600px;
}

.selected-tags-container {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.selected-tags-container label {
  display: block;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 14px;
}

/* 標籤來源頁籤 */
.tag-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding: 4px;
  background: #f8f9fa;
  border-radius: 8px;
}

.tag-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-tab:hover {
  background: white;
  color: #495057;
}

.tag-tab.active {
  background: white;
  color: #556ee6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.tag-tab i {
  font-size: 16px;
}

.selected-tags-container label {
  display: block;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 14px;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-tag-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  font-size: 13px;
  color: #333;
}

.selected-tag-item i:first-child {
  font-size: 14px;
}

.remove-tag-btn {
  background: none;
  border: none;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  color: #dc3545;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: all 0.2s;
}

.remove-tag-btn:hover {
  background: #dc3545;
  color: white;
}

.remove-tag-btn i {
  font-size: 12px;
}

.tags-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
}

.tag-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-list-item:last-child {
  border-bottom: none;
}

.tag-list-item:hover:not(.disabled) {
  background: #f8f9fa;
}

.tag-list-item.selected {
  background: #e7f3ff;
}

.tag-list-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8f9fa;
}

.tag-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.tag-info i {
  font-size: 18px;
}

.tag-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.tag-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
}

/* 標籤類型 badge */
.manual-badge {
  background-color: #fff3e0;
  color: #e65100;
}

.auto-badge {
  background-color: #e3f2fd;
  color: #1565c0;
}

.system-badge {
  background-color: #f3e5f5;
  color: #6a1b9a;
}

.temp-badge {
  background-color: #fff9c4;
  color: #f57c00;
}

.added-badge {
  background-color: #e0e0e0;
  color: #666;
}

/* 互動標籤高亮顯示 */
.tag-list-item.manual-tag:not(.disabled) {
  border-left: 3px solid transparent;
  transition: all 0.2s;
}

.tag-list-item.manual-tag:not(.disabled):hover {
  background-color: #fff3e0;
  border-left-color: #ff9800;
}

.temp-badge {
  background: #fff3cd;
  color: #856404;
}

.added-badge {
  background: #d1ecf1;
  color: #0c5460;
}

.check-icon {
  color: #667eea;
  font-size: 20px;
  display: flex;
  align-items: center;
}

.tags-list .empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #999;
}

</style>

<style>
.page-header-member-detail {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-member-detail {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-member-detail {
    left: 0 !important;
    top: 56px;
  }
}
</style>
